import { useEffect, useState } from 'react';

interface UseAutocompleteNavigationProps {
  optionCount: number;
  isOpen: boolean;
  onSelect: (index: number) => void;
  onClose: () => void;
  /**
   * Optional index to highlight when the popover opens. When set, Enter
   * commits this option immediately without requiring an ArrowDown press.
   * Re-applied each time `isOpen` transitions false → true.
   */
  defaultFocusedIndex?: number;
}

/**
 * Manages virtual keyboard focus for an autocomplete dropdown.
 *
 * While the popover is open:
 * - ArrowDown / ArrowUp move the highlighted option index.
 * - Enter selects the highlighted option (calls `onSelect(index)`).
 * - Escape closes the popover (calls `onClose()`).
 * - ArrowLeft / ArrowRight clear the highlight so the next press
 *   falls through to grid token navigation.
 *
 * The returned `containerDataProps` must be spread onto the token container
 * element so that `useQueryBuilderGridItem` can detect active virtual focus
 * and block ArrowLeft / ArrowRight grid navigation while an option is highlighted.
 */
export function useAutocompleteNavigation({
  optionCount,
  isOpen,
  onSelect,
  onClose,
  defaultFocusedIndex,
}: UseAutocompleteNavigationProps): {
  focusedIndex: number;
  containerDataProps: { 'data-autocomplete-active'?: string };
  handleKeyDown: (e: React.KeyboardEvent) => void;
} {
  const [rawFocusedIndex, setFocusedIndex] = useState(defaultFocusedIndex ?? -1);

  // Re-seed the highlight on each open transition. Without this, the rawFocusedIndex
  // would persist across popover sessions (a stale highlight from the previous open),
  // and the `defaultFocusedIndex` wouldn't take effect because useState only honours
  // its initial value once. Reading the latest defaultFocusedIndex from a closure
  // here keeps the seed in sync with the caller's current intent.
  useEffect(() => {
    if (isOpen) {
      setFocusedIndex(defaultFocusedIndex ?? -1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-seed on open transition; defaultFocusedIndex is read via closure
  }, [isOpen]);

  // Derive the effective index instead of resetting via effect, so the value is
  // always coherent with isOpen / optionCount and we never read a stale frame:
  //   - when the popover is closed, no option is focused
  //   - when option list shrinks below the stored index, fall back to no focus
  //     (avoids onSelect(staleIndex) crashing on optionCount === 0 or out-of-bounds)
  const focusedIndex = !isOpen || rawFocusedIndex >= optionCount ? -1 : rawFocusedIndex;

  const containerDataProps: { 'data-autocomplete-active'?: string } =
    focusedIndex >= 0 ? { 'data-autocomplete-active': 'true' } : {};

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!isOpen) {
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onClose();
      return;
    }

    if (optionCount === 0) {
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        e.stopPropagation();
        setFocusedIndex((i) => (i + 1) % optionCount);
        break;
      case 'ArrowUp':
        e.preventDefault();
        e.stopPropagation();
        setFocusedIndex((i) => (i <= 0 ? optionCount - 1 : i - 1));
        break;
      case 'Enter':
        if (focusedIndex >= 0) {
          e.preventDefault();
          e.stopPropagation();
          onSelect(focusedIndex);
        }
        break;
      case 'ArrowLeft':
      case 'ArrowRight':
        // Clear virtual focus; the capture-phase guard in useQueryBuilderGridItem
        // already called e.preventDefault() so grid navigation is blocked this press.
        // On the next ArrowLeft/Right press the guard won't fire and grid nav resumes.
        if (focusedIndex >= 0) {
          setFocusedIndex(-1);
        }
        break;
      default:
        break;
    }
  }

  return { focusedIndex, containerDataProps, handleKeyDown };
}
